import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import '../AdvertiserDashboardNewCampaign/AdvertiserDashboardNewCampaign.css';
import { usePlatformAdvertisingAgreement } from '../../../../hooks/usePlatformAdvertisingAgreement';
import { platformAgreementPath } from '../../../../utils/platformAgreementUrl';
import { useSelector } from 'react-redux';
import { getToken } from '../../../../features/token/tokenSaver';
import ScreenSelector from '../ScreenSelector/ScreenSelector';
import PaymentButton from '../AdvertiserDashboardNewCampaign/PaymentButton/PaymentButton';
import { ALL_SCREENS_BUNDLE_USD, PLAYS_PER_SCREEN_MONTH } from '../../../../utils/advertiserScreenPricing';

function nextMonthLabel() {
    const d = new Date();
    d.setUTCMonth(d.getUTCMonth() + 1, 1);
    return d.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export default function AdvertiserDashboardPrepay() {
    const [searchParams] = useSearchParams();
    const token = useSelector(getToken);
    const [selectedScreenIds, setSelectedScreenIds] = useState([]);
    const [fleetSize, setFleetSize] = useState(1);
    const [monthlyFee, setMonthlyFee] = useState(0);
    const [screenSelectionError, setScreenSelectionError] = useState('');
    const [showPayment, setShowPayment] = useState(false);
    const [pendingRedirect, setPendingRedirect] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [tosAccepted, setTosAccepted] = useState(false);
    const [tosError, setTosError] = useState('');

    const agreementOptions = {
        screenCount: selectedScreenIds.length,
        selectedScreenIds,
        fleetSize,
    };
    const { doc: agreementDoc } = usePlatformAdvertisingAgreement(
        monthlyFee || ALL_SCREENS_BUNDLE_USD,
        agreementOptions
    );
    const tosVersion = agreementDoc?.version;
    const agreementLink = platformAgreementPath(monthlyFee || ALL_SCREENS_BUNDLE_USD, agreementOptions);
    const periodLabel = nextMonthLabel();

    useEffect(() => {
        if (searchParams.get('stripe') === 'cancelled') {
            setSubmitError('Checkout was cancelled. You can try again when you are ready.');
        }
    }, [searchParams]);

    function handleScreenSelectionChange(selection) {
        setSelectedScreenIds(selection.screenIds);
        setFleetSize(selection.fleetSize || 1);
        setMonthlyFee(selection.totalUsd);
        if (selection.screenIds.length > 0) {
            setScreenSelectionError('');
        }
    }

    function onReserve(e) {
        e.preventDefault();
        if (!selectedScreenIds.length) {
            setScreenSelectionError('Select at least one screen to reserve your spot.');
            return;
        }
        setTosAccepted(false);
        setTosError('');
        setShowPayment(true);
    }

    function closePaymentModal() {
        setShowPayment(false);
        setTosAccepted(false);
        setTosError('');
    }

    const pendingFormData = {
        campaignName: `Prepaid reservation — ${periodLabel}`,
        monthlyFee,
        selectedScreenIds,
        storefront: 1,
    };

    return (
        <div className="advertiserDashboardContainerCampaigns">
            {submitError && (
                <>
                    <div className="campaignModalAlert">
                        <span className="registeredScreensTitle advertiserListTitle">Notice</span>
                        <p style={{ padding: '1rem', textAlign: 'center', color: '#C52F00' }}>{submitError}</p>
                        <input
                            type="button"
                            onClick={() => setSubmitError('')}
                            className="advertiserRegisterSubmitBtn"
                            value="Close"
                        />
                    </div>
                    <div className="campaignModalBackdrop" />
                </>
            )}

            <div className="adminDashboardScreenPageContainer">
                <span className="registeredScreensTitle advertiserListTitle">Prepay for next month</span>

                <div className="monthlyFeeBanner">
                    <span className="monthlyFeeText">Reserve advertising for {periodLabel}</span>
                    <span className="monthlyFeeSubtext">
                        Pay now to hold your spot on the screens you choose. Upload your campaign creative anytime before the period starts.
                    </span>
                    <span className="monthlyFeeNote">
                        Each screen includes {PLAYS_PER_SCREEN_MONTH.toLocaleString()} plays per month (180 plays/screen/day × 30 days). Select all Invert screens for ${ALL_SCREENS_BUNDLE_USD.toFixed(2)}.
                    </span>
                </div>

                <form onSubmit={onReserve}>
                    <ScreenSelector
                        selectedScreenIds={selectedScreenIds}
                        onSelectionChange={handleScreenSelectionChange}
                        selectionError={screenSelectionError}
                    />

                    <input
                        type="submit"
                        className="advertiserRegisterSubmitBtn"
                        value={selectedScreenIds.length > 0 ? 'Continue to payment' : 'Select screens to continue'}
                        disabled={selectedScreenIds.length === 0}
                    />
                </form>
            </div>

            {showPayment && (
                <div className="campaignModalAlert" style={{ maxWidth: '800px', width: 'min(92vw, 800px)', maxHeight: '90dvh' }}>
                    <span className="registeredScreensTitle advertiserListTitle">Complete prepayment</span>
                    <p style={{ padding: '1rem', textAlign: 'center' }}>
                        One-time payment: ${monthlyFee.toFixed(2)} for {periodLabel}
                    </p>
                    <p style={{ padding: '0 1rem 1rem', textAlign: 'center', fontSize: '0.9rem' }}>
                        No creative upload required today. After payment you can add your campaign from My Campaigns.
                    </p>
                    {pendingRedirect ? (
                        <div className="campaignModalLoadingOverlay">
                            <p style={{ marginBottom: '1rem', color: '#4CAF50' }}>Redirecting to secure checkout…</p>
                        </div>
                    ) : (
                        <div style={{ padding: '1rem' }}>
                            <label className="campaign-tos-checkbox">
                                <input
                                    type="checkbox"
                                    checked={tosAccepted}
                                    onChange={(e) => {
                                        setTosAccepted(e.target.checked);
                                        if (e.target.checked) setTosError('');
                                    }}
                                />
                                <span>
                                    I agree to the{' '}
                                    <Link
                                        to={agreementLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="campaign-tos-link"
                                        onClick={(ev) => ev.stopPropagation()}
                                    >
                                        INVERT Advertising Agreement
                                    </Link>{' '}
                                    {tosVersion ? `(version ${tosVersion})` : ''} for this prepaid reservation.
                                </span>
                            </label>
                            {tosError && <p className="campaign-tos-error">{tosError}</p>}
                            <PaymentButton
                                setPendingRedirect={setPendingRedirect}
                                data={pendingFormData}
                                cost={monthlyFee.toFixed(2)}
                                billingMode="one_time_prepay"
                                tosAccepted={tosAccepted}
                                onTosError={setTosError}
                                onCheckoutError={(msg) => {
                                    setSubmitError(msg);
                                    closePaymentModal();
                                }}
                            />
                            <button
                                type="button"
                                onClick={closePaymentModal}
                                className="advertiserRegisterSubmitBtn"
                                style={{ marginTop: '1rem', backgroundColor: '#666' }}
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>
            )}
            {showPayment && <div className="campaignModalBackdrop" />}
        </div>
    );
}
